import { t } from '@lingui/macro';
import _ from 'lodash';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';

// Form field path constants
export const TEST_TYPE_ICON_FIELD = 'details.icon';
export const TEST_TYPE_TITLE_FIELD = 'details.title';
export const TEST_TYPE_DESCRIPTION_FIELD = 'details.description';
export const TEST_TYPE_TIMEOUT_FIELD = 'details.testTimeOutSec';
export const TEST_TYPE_SLICE_PACKETS_FIELD = 'slicePackets';
export const TEST_TYPE_BTT_TEST_FIELD = 'bttTest';
export const TEST_TYPE_SHOW_BTT_AUTH_FIELD = 'showBttAuth';
export const TEST_TYPE_SCRIPT_FIELD = 'script';
export const TEST_TYPE_METRICS_FIELD = 'metrics';
export const TEST_TYPE_THRESHOLDS_FIELD = 'thresholds';
export const TEST_TYPE_CHART_Y_AXIS_FIELD = 'chartConfig.yAxis';
export const TEST_TYPE_CHART_Y_AXIS_TITLE_LEFT_FIELD = 'chartConfig.yAxis.0.title.text';
export const TEST_TYPE_CHART_Y_AXIS_TITLE_RIGHT_FIELD = 'chartConfig.yAxis.1.title.text';
export const TEST_TYPE_CHART_TYPE_FIELD = 'chartConfig.chart.type';
export const TEST_TYPE_CHART_STACKING_FIELD = 'chartConfig.plotOptions.series.stacking';
export const TEST_TYPE_TEST_BUILDER_SECTIONS_FIELD = 'testBuilder.details.sections';

// Metric / threshold field names
export const METRIC_NAME_FIELD = 'name';
export const METRIC_TITLE_FIELD = 'title';
export const METRIC_UNITS_FIELD = 'units';
export const METRIC_MULTIPLIER_FIELD = 'multiplier';
export const THRESHOLD_METRIC_IDS_FIELD = 'metricIds';
export const THRESHOLD_INVERTED_FIELD = 'inverted';
export const THRESHOLD_MIN_FIELD = 'min';
export const THRESHOLD_DEGRADED_FIELD = 'degraded';
export const THRESHOLD_CRITICAL_FIELD = 'critical';
export const THRESHOLD_MAX_FIELD = 'max';

// Example script download title (mirrors portal/ui en.js exampleScriptTitle)
export const EXAMPLE_SCRIPT_TITLE = t`nGeniusPULSE Custom Test Examples`;

// Enums
export const RESERVED_METRICS = [
    'availability',
    'resolved_ip_address',
    'message',
    'status',
    'ispass',
    'statuscounts',
    'wifi_ssid',
];

export const CHART_TYPES = ['area', 'line', 'scatter'];
export const CHART_STACKING_DEFAULT_KEY = 'null';
export const CHART_STACKINGS = [
    { key: CHART_STACKING_DEFAULT_KEY, label: t`No stacking (overlaid)` },
    { key: 'normal', label: t`Stacked (normal)` },
    { key: 'percent', label: t`Stacked (percent)` },
];

export const BASE_INPUT_TYPES = ['boolean', 'number', 'integer', 'password', 'radio', 'select', 'text', 'hostname'];
export const WIFI_INPUT_TYPES = ['wi-fi networks', 'wi-fi tests'];

// Metrics table columns (static; BTT-specific column is appended at the call site).
export const metricColumns = [
    {
        mapping: METRIC_TITLE_FIELD,
        sortable: true,
        title: t`Metric Label`,
        width: 180,
    },
    {
        mapping: METRIC_NAME_FIELD,
        sortable: true,
        title: t`Metric Name`,
        width: 180,
    },
    {
        type: COLUMN_TYPES.TEXT,
        sortable: true,
        mapping: 'isMain',
        title: t`Primary Metric`,
        width: 150,
        formatter: (value) => _.capitalize(value),
    },
    {
        type: COLUMN_TYPES.TEXT,
        mapping: 'hidden',
        sortable: true,
        title: t`Charted`,
        width: 100,
        formatter: (value) => _.capitalize(!value),
    },
    {
        type: COLUMN_TYPES.STATUS,
        mapping: 'color',
        title: t`Chart Color`,
        width: 120,
        renderer: (row) => (
            <div
                style={{
                    display: row.hidden ? 'none' : 'inline-block',
                    height: '16px',
                    width: '16px',
                    background: row.color,
                }}
            />
        ),
    },
    {
        mapping: METRIC_UNITS_FIELD,
        sortable: true,
        title: t`Metric Units Label`,
        width: 160,
    },
    {
        mapping: METRIC_MULTIPLIER_FIELD,
        sortable: true,
        title: t`Metric Precision`,
        width: 150,
    },
];

// Threshold table columns factory
export const getThresholdColumns = (metrics) => {
    // Pre-index metrics by numeric id so the formatter is O(1) per row.
    const metricById = new Map(_.map(metrics, (m) => [_.toNumber(m?.id), m]));
    return [
        {
            mapping: THRESHOLD_METRIC_IDS_FIELD,
            formatter: (value) => {
                const metric = metricById.get(_.toNumber(_.first(value)));
                return metric?.[METRIC_TITLE_FIELD] || metric?.[METRIC_NAME_FIELD] || '';
            },
            title: t`Metric`,
            width: 220,
        },
        {
            mapping: THRESHOLD_INVERTED_FIELD,
            title: t`Inverted`,
            width: 120,
            formatter: (value) => _.capitalize(value),
        },
        { mapping: THRESHOLD_MIN_FIELD, title: t`Min`, width: 100 },
        { mapping: THRESHOLD_DEGRADED_FIELD, title: t`Degraded`, width: 100 },
        { mapping: THRESHOLD_CRITICAL_FIELD, title: t`Excessive`, width: 100 },
        { mapping: THRESHOLD_MAX_FIELD, title: t`Max`, width: 100 },
    ];
};

// Default form values for new test type
export const defaultValues = {
    metrics: [],
    runtime: 'python',
    chartConfig: {
        yAxis: [{ title: { text: '' } }],
        chart: { type: 'line' },
        plotOptions: { series: { stacking: CHART_STACKING_DEFAULT_KEY } },
    },
    testBuilder: {
        details: {
            sections: [],
        },
    },
    testTimeOutSec: 45,
    details: {
        title: '',
        icon: '',
        description: '',
        testTimeOutSec: 45,
    },
    bttTest: false,
    showBttAuth: false,
    supportsInterfaceSelection: true,
    slicePackets: true,
    thresholds: [],
    converted: false,
    originalScript: '',
    scriptConvertErr: '',
    metricsFromSteps: [],
};
